import React, { useState, useEffect } from 'react';
import { policiesAPI } from '@/services/api';
import api from '@/services/api';
import { approvePolicy } from '@/services/grc';
import { getRoles, listTenantMembers } from '@/services/admin';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Pagination from '@/components/Pagination';
import { 
  Plus, FileText, Edit, Trash2, Eye, Save, Download, 
  CheckCircle, Clock, AlertCircle, Search, Filter,
  FileCode, FilePlus, History, Users, XCircle, RotateCcw, Send, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import DocumentEditor from '@/components/DocumentEditor';


const PoliciesPage = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFramework, setFilterFramework] = useState('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    framework: 'SOC 2',
    control_ids: [],
    owner: '',
    status: 'draft',
    approver_type: 'role', // 'role' | 'user'
    approver_role: '',
    approver_user_id: '',
    require_acknowledgement: false,
    assignment_scope: 'all', // 'all' | 'specific'
    assigned_user_ids: [],
  });

  // RBAC: roles & tenant users (for approver selection)
  const [rbacRoles, setRbacRoles] = useState([]);
  const [tenantUsers, setTenantUsers] = useState([]);

  // Backend policy templates (replaces hard-coded policyTemplates dict)
  const [backendTemplates, setBackendTemplates] = useState([]);
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateFrameworkFilter, setTemplateFrameworkFilter] = useState('all');
  const [templateLoading, setTemplateLoading] = useState(false);

  useEffect(() => {
    fetchPolicies();
  }, [currentPage, pageSize, filterFramework]);

  useEffect(() => {
    // Load approver options once
    (async () => {
      try {
        const rolesRes = await getRoles();
        setRbacRoles(rolesRes.roles || []);
      } catch (e) { /* best-effort */ }
      try {
        const tid = localStorage.getItem('currentTenantId');
        if (tid) {
          const mem = await listTenantMembers(tid, { limit: 100 });
          setTenantUsers(mem.items || []);
        }
      } catch (e) { /* best-effort */ }
    })();
  }, []);

  // Load backend policy templates once on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    setTemplateLoading(true);
    fetch(`${API_URL}/api/policy-templates/templates`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setBackendTemplates(Array.isArray(data) ? data : []))
      .catch(() => setBackendTemplates([]))
      .finally(() => setTemplateLoading(false));
  }, []);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        page_size: pageSize
      };
      if (filterFramework !== 'all') {
        params.framework = filterFramework;
      }
      const response = await policiesAPI.list(params);
      // Handle paginated response
      if (response.data.items) {
        setPolicies(response.data.items);
        setTotalItems(response.data.total);
        setTotalPages(response.data.total_pages);
      } else {
        setPolicies(response.data);
        setTotalItems(response.data.length);
        setTotalPages(1);
      }
    } catch (error) {
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Normalize approver: only send the selected type's id
      const payload = { ...formData };
      if (payload.approver_type === 'role') payload.approver_user_id = '';
      else payload.approver_role = '';
      delete payload.approver_type;
      // Clear assignment scope defaults if not required
      if (!payload.require_acknowledgement) {
        payload.assignment_scope = 'all';
        payload.assigned_user_ids = [];
      }

      if (selectedPolicy) {
        const res = await policiesAPI.update(selectedPolicy.id, payload);
        if (res?.data?.reapproval_required) {
          toast.warning('Policy content changed — status reverted to Draft and re-approval is required.');
        } else {
          toast.success('Policy updated successfully');
        }
      } else {
        await policiesAPI.create(payload);
        toast.success('Policy created successfully');
      }
      setIsDialogOpen(false);
      setSelectedPolicy(null);
      resetForm();
      fetchPolicies();
    } catch (error) {
      toast.error('Failed to save policy');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this policy?')) {
      try {
        await policiesAPI.delete(id);
        toast.success('Policy deleted');
        fetchPolicies();
      } catch (error) {
        toast.error('Failed to delete policy');
      }
    }
  };

  const handleApprovePolicy = async (policyId, action) => {
    try {
      await approvePolicy(policyId, action, '');
      toast.success(`Policy ${action}`);
      fetchPolicies();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} policy`);
    }
  };

  const handleEdit = (policy) => {
    setSelectedPolicy(policy);
    setFormData({
      title: policy.title,
      description: policy.description,
      content: policy.content || '',
      framework: policy.framework,
      control_ids: policy.control_ids || [],
      owner: policy.owner,
      status: policy.status || 'draft',
      approver_type: policy.approver_user_id ? 'user' : 'role',
      approver_role: policy.approver_role || '',
      approver_user_id: policy.approver_user_id || '',
      require_acknowledgement: !!policy.require_acknowledgement,
      assignment_scope: policy.assignment_scope || 'all',
      assigned_user_ids: policy.assigned_user_ids || [],
    });
    setIsEditorOpen(true);
  };

  const handleView = (policy) => {
    setSelectedPolicy(policy);
    setIsEditorOpen(true);
  };

  const handleSaveContent = async (content) => {
    if (selectedPolicy) {
      try {
        const res = await policiesAPI.update(selectedPolicy.id, { content });
        if (res?.data?.reapproval_required) {
          toast.warning('Content saved. Previous approval invalidated — re-approval required.');
        } else {
          toast.success('Policy content saved');
        }
        setSelectedPolicy(prev => ({
          ...prev,
          content,
          version: res.data?.version || prev.version,
          status: res.data?.status || prev.status,
        }));
        fetchPolicies();
      } catch (error) {
        toast.error('Failed to save policy content');
      }
    }
  };

  const handleExportPdf = async (policy) => {
    try {
      const res = await policiesAPI.exportPdf(policy.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${policy.title.replace(/\s/g, '_')}_v${policy.version || 1}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exported');
    } catch {
      toast.error('Export failed');
    }
  };

  const handleSubmitForReview = async (policy) => {
    try {
      await policiesAPI.submitForReview(policy.id);
      toast.success('Submitted for review');
      fetchPolicies();
      if (selectedPolicy?.id === policy.id) {
        setSelectedPolicy(prev => ({ ...prev, status: 'pending' }));
      }
    } catch {
      toast.error('Failed to submit');
    }
  };

  const handleUseTemplate = async (template) => {
    // template is a backend template item: { id, name, frameworks, description, ... }
    if (!template || !template.id) return;
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const orgName = encodeURIComponent(localStorage.getItem('organization_name') || '[Organization Name]');
      const res = await fetch(
        `${API_URL}/api/policy-templates/templates/${template.id}/preview?format=html&organization_name=${orgName}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Preview failed');
      const data = await res.json();
      // Map first backend framework to our UI framework select if possible
      const frameworkMap = {
        'ISO27001': 'ISO 27001',
        'ISO42001': 'ISO 42001',
        'SOC2': 'SOC 2',
        'SOC3': 'SOC 3',
        'GDPR': 'GDPR',
        'CCPA': 'CCPA',
        'PCI-DSS': 'PCI DSS',
        'HIPAA': 'HIPAA',
        'NIST': 'NIST CSF',
        'NIST-CSF-2.0': 'NIST CSF',
      };
      const mappedFramework = (template.frameworks || [])
        .map((f) => frameworkMap[f])
        .find(Boolean);
      setFormData((prev) => ({
        ...prev,
        title: template.name,
        description: template.description || prev.description,
        content: data.preview_content || '',
        framework: mappedFramework || prev.framework,
      }));
      toast.success(`Template "${template.name}" loaded`);
    } catch (e) {
      toast.error('Failed to load template');
    }
  };

  // One-click: create a full policy straight from the template (no manual steps)
  const handleQuickGenerate = async (template, e) => {
    if (e) e.stopPropagation();
    if (!template || !template.id) return;
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(
        `${API_URL}/api/policy-templates/templates/${template.id}/quick-generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!res.ok) {
        const errBody = await res.text();
        throw new Error(errBody || 'Generation failed');
      }
      const data = await res.json();
      const linked = data.linked_controls ?? 0;
      toast.success(
        `Policy "${data.policy?.title || template.name}" created${linked > 0 ? ` • Linked to ${linked} controls` : ''}`
      );
      // Close dialog, refresh, and auto-open the new policy in the editor
      setIsDialogOpen(false);
      resetForm();
      await fetchPolicies();
      if (data.policy) {
        setSelectedPolicy(data.policy);
        setIsEditorOpen(true);
      }
    } catch (err) {
      toast.error('Failed to generate policy from template');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      content: '',
      framework: 'SOC 2',
      control_ids: [],
      owner: '',
      status: 'draft',
      approver_type: 'role',
      approver_role: '',
      approver_user_id: '',
      require_acknowledgement: false,
      assignment_scope: 'all',
      assigned_user_ids: [],
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'pending': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'draft': return <AlertCircle className="w-4 h-4 text-gray-400" />;
      default: return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'draft': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Client-side search filter (framework is handled server-side)
  const filteredPolicies = policies.filter(policy => {
    if (!searchTerm) return true;
    const matchesSearch = (policy.title && policy.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (policy.description && policy.description.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  if (isEditorOpen && selectedPolicy) {
    return (
      <PolicyEditorView
        policy={selectedPolicy}
        onBack={() => { setIsEditorOpen(false); setSelectedPolicy(null); fetchPolicies(); }}
        onSave={handleSaveContent}
        onContentChange={(content) => setSelectedPolicy(prev => ({ ...prev, content }))}
        onExport={handleExportPdf}
        onSubmitReview={handleSubmitForReview}
        onApprove={handleApprovePolicy}
        getStatusColor={getStatusColor}
      />
    );
  }

  return (
    <div className="p-6 md:p-8 lg:p-12" data-testid="policies-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl font-bold font-heading tracking-tight mb-2">Policy Management</h1>
          <p className="text-sm text-muted-foreground">Create, edit, and manage compliance policies with built-in document editor</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-policy-button">
                <Plus className="w-4 h-4 mr-2" />
                Create Policy
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{selectedPolicy ? 'Edit Policy' : 'Create New Policy'}</DialogTitle>
              </DialogHeader>
              
              <Tabs defaultValue="details" className="mt-4">
                <TabsList>
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="templates">Templates</TabsTrigger>
                  <TabsTrigger value="content">Content Editor</TabsTrigger>
                </TabsList>
                
                <TabsContent value="details">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="title">Policy Title *</Label>
                        <Input
                          id="title"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          required
                          placeholder="e.g., Information Security Policy"
                        />
                      </div>
                      <div>
                        <Label htmlFor="owner">Policy Owner *</Label>
                        <Input
                          id="owner"
                          value={formData.owner}
                          onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                          required
                          placeholder="e.g., CISO"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="description">Description</Label>
                      <textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        placeholder="Brief description of the policy purpose"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="framework">Compliance Framework</Label>
                        <Select
                          value={formData.framework}
                          onValueChange={(value) => setFormData({ ...formData, framework: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="SOC 2">SOC 2</SelectItem>
                            <SelectItem value="SOC 3">SOC 3</SelectItem>
                            <SelectItem value="ISO 27001">ISO 27001</SelectItem>
                            <SelectItem value="ISO 42001">ISO 42001</SelectItem>
                            <SelectItem value="GDPR">GDPR</SelectItem>
                            <SelectItem value="CCPA">CCPA</SelectItem>
                            <SelectItem value="PCI DSS">PCI DSS</SelectItem>
                            <SelectItem value="USPCF">USPCF</SelectItem>
                            <SelectItem value="HIPAA">HIPAA</SelectItem>
                            <SelectItem value="NIST CSF">NIST CSF</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="status">Status</Label>
                        <Select
                          value={formData.status}
                          onValueChange={(value) => setFormData({ ...formData, status: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="pending">Pending Review</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Approver selection */}
                    <div className="border border-border rounded-md p-4 space-y-3 bg-muted/30" data-testid="approver-section">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-semibold">Approver</Label>
                        <div className="inline-flex rounded-md border border-border bg-background p-0.5 text-xs">
                          <button
                            type="button"
                            data-testid="approver-type-role"
                            className={`px-3 py-1 rounded-sm transition-colors ${formData.approver_type === 'role' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
                            onClick={() => setFormData({ ...formData, approver_type: 'role', approver_user_id: '' })}
                          >
                            By Role
                          </button>
                          <button
                            type="button"
                            data-testid="approver-type-user"
                            className={`px-3 py-1 rounded-sm transition-colors ${formData.approver_type === 'user' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
                            onClick={() => setFormData({ ...formData, approver_type: 'user', approver_role: '' })}
                          >
                            By User
                          </button>
                        </div>
                      </div>
                      {formData.approver_type === 'role' ? (
                        <Select
                          value={formData.approver_role || ''}
                          onValueChange={(value) => setFormData({ ...formData, approver_role: value })}
                        >
                          <SelectTrigger data-testid="approver-role-select">
                            <SelectValue placeholder="Select a role from RBAC" />
                          </SelectTrigger>
                          <SelectContent>
                            {rbacRoles.length === 0 ? (
                              <SelectItem value="__none__" disabled>No roles available</SelectItem>
                            ) : rbacRoles.map((r) => (
                              <SelectItem key={r.role} value={r.role} data-testid={`approver-role-option-${r.role}`}>
                                <span className="capitalize">{r.role.replace(/_/g, ' ')}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Select
                          value={formData.approver_user_id || ''}
                          onValueChange={(value) => setFormData({ ...formData, approver_user_id: value })}
                        >
                          <SelectTrigger data-testid="approver-user-select">
                            <SelectValue placeholder="Select a user from tenant" />
                          </SelectTrigger>
                          <SelectContent>
                            {tenantUsers.length === 0 ? (
                              <SelectItem value="__none__" disabled>No users available</SelectItem>
                            ) : tenantUsers.map((u) => (
                              <SelectItem key={u.user_id} value={u.user_id} data-testid={`approver-user-option-${u.user_id}`}>
                                {u.full_name || u.email} {u.role ? <span className="text-xs text-muted-foreground ml-1">({u.role})</span> : null}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Roles are sourced from <span className="font-medium">Settings → Roles &amp; Permissions (RBAC)</span>. Editing content after approval will revert the policy to Draft and require re-approval.
                      </p>
                    </div>

                    {/* Require acknowledgement section */}
                    <div className="border border-border rounded-md p-4 space-y-3 bg-muted/30" data-testid="ack-section">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-sm font-semibold">Require user acknowledgement</Label>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            When enabled, users must review and acknowledge this policy after publish.
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer" data-testid="require-ack-toggle">
                          <input
                            type="checkbox"
                            checked={formData.require_acknowledgement}
                            onChange={(e) => setFormData({ ...formData, require_acknowledgement: e.target.checked })}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                        </label>
                      </div>
                      {formData.require_acknowledgement && (
                        <>
                          <div>
                            <Label className="text-xs">Assign to</Label>
                            <div className="inline-flex rounded-md border border-border bg-background p-0.5 text-xs mt-1">
                              <button
                                type="button"
                                data-testid="ack-scope-all"
                                className={`px-3 py-1 rounded-sm transition-colors ${formData.assignment_scope === 'all' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
                                onClick={() => setFormData({ ...formData, assignment_scope: 'all', assigned_user_ids: [] })}
                              >
                                All tenant users
                              </button>
                              <button
                                type="button"
                                data-testid="ack-scope-specific"
                                className={`px-3 py-1 rounded-sm transition-colors ${formData.assignment_scope === 'specific' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
                                onClick={() => setFormData({ ...formData, assignment_scope: 'specific' })}
                              >
                                Specific users
                              </button>
                            </div>
                          </div>
                          {formData.assignment_scope === 'specific' && (
                            <div>
                              <Label className="text-xs">Select users</Label>
                              <div className="max-h-40 overflow-y-auto border rounded p-2 space-y-1 bg-background">
                                {tenantUsers.length === 0 ? (
                                  <p className="text-xs text-muted-foreground">No users available</p>
                                ) : tenantUsers.map((u) => {
                                  const uid = u.user_id;
                                  const checked = (formData.assigned_user_ids || []).includes(uid);
                                  return (
                                    <label key={uid} className="flex items-center gap-2 text-xs py-1 cursor-pointer" data-testid={`ack-user-${uid}`}>
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e) => {
                                          const set = new Set(formData.assigned_user_ids || []);
                                          if (e.target.checked) set.add(uid); else set.delete(uid);
                                          setFormData({ ...formData, assigned_user_ids: Array.from(set) });
                                        }}
                                      />
                                      <span>{u.full_name || u.email}</span>
                                      <span className="text-muted-foreground">({u.email})</span>
                                    </label>
                                  );
                                })}
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                Selected: {(formData.assigned_user_ids || []).length} user(s)
                              </p>
                            </div>
                          )}
                          <p className="text-xs text-indigo-600">
                            Assignments are created when you <strong>Publish</strong> the policy (draft edits do not send notifications).
                          </p>
                        </>
                      )}
                    </div>
                    
                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }}>
                        Cancel
                      </Button>
                      <Button type="submit">
                        {selectedPolicy ? 'Update Policy' : 'Create Policy'}
                      </Button>
                    </div>
                  </form>
                </TabsContent>
                
                <TabsContent value="templates">
                  {/* Search + framework filter */}
                  <div className="flex flex-col md:flex-row gap-2 mb-4" data-testid="templates-filters">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        value={templateSearch}
                        onChange={(e) => setTemplateSearch(e.target.value)}
                        placeholder="Search templates by name or description..."
                        className="pl-10"
                        data-testid="template-search-input"
                      />
                    </div>
                    <Select value={templateFrameworkFilter} onValueChange={setTemplateFrameworkFilter}>
                      <SelectTrigger className="w-full md:w-[220px]" data-testid="template-framework-filter">
                        <SelectValue placeholder="All Frameworks" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Frameworks</SelectItem>
                        {Array.from(
                          new Set(backendTemplates.flatMap((t) => t.frameworks || []))
                        )
                          .sort()
                          .map((fw) => (
                            <SelectItem key={fw} value={fw}>
                              {fw}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Template grid */}
                  {templateLoading ? (
                    <div className="text-center py-10 text-sm text-muted-foreground">Loading templates…</div>
                  ) : (() => {
                    const filtered = backendTemplates.filter((t) => {
                      if (templateFrameworkFilter !== 'all' && !(t.frameworks || []).includes(templateFrameworkFilter)) return false;
                      if (!templateSearch) return true;
                      const q = templateSearch.toLowerCase();
                      return (
                        (t.name && t.name.toLowerCase().includes(q)) ||
                        (t.description && t.description.toLowerCase().includes(q)) ||
                        (t.category && t.category.toLowerCase().includes(q))
                      );
                    });
                    if (filtered.length === 0) {
                      return (
                        <div className="text-center py-10 text-sm text-muted-foreground" data-testid="no-templates-message">
                          No templates match your filters.
                        </div>
                      );
                    }
                    return (
                      <>
                        <p className="text-xs text-muted-foreground mb-3" data-testid="template-count">
                          Showing {filtered.length} of {backendTemplates.length} templates
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[50vh] overflow-y-auto pr-1" data-testid="templates-grid">
                          {filtered.map((t) => (
                            <Card
                              key={t.id}
                              className="cursor-pointer hover:border-primary hover:shadow-md transition-all"
                              onClick={() => handleUseTemplate(t)}
                              data-testid={`template-card-${t.id}`}
                            >
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-semibold flex items-start gap-2 leading-snug">
                                  <FileCode className="w-4 h-4 shrink-0 mt-0.5 text-blue-500" />
                                  <span className="flex-1">{t.name}</span>
                                </CardTitle>
                              </CardHeader>
                              <CardContent className="pt-0">
                                <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                                  {t.description}
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {(t.frameworks || []).slice(0, 4).map((fw) => (
                                    <Badge key={fw} variant="outline" className="h-5 text-[10px] px-1.5">
                                      {fw}
                                    </Badge>
                                  ))}
                                  {(t.frameworks || []).length > 4 && (
                                    <Badge variant="outline" className="h-5 text-[10px] px-1.5">
                                      +{t.frameworks.length - 4}
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center justify-between mt-2">
                                  <span className="text-[11px] text-muted-foreground capitalize">
                                    {(t.category || '').replace(/_/g, ' ')}
                                  </span>
                                  <span className="text-[11px] text-muted-foreground">
                                    {t.section_count} sections
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/50">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="flex-1 h-7 text-xs"
                                    onClick={(e) => { e.stopPropagation(); handleUseTemplate(t); }}
                                    data-testid={`template-load-${t.id}`}
                                  >
                                    <Edit className="w-3 h-3 mr-1" />
                                    Load to Editor
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="flex-1 h-7 text-xs bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                                    onClick={(e) => handleQuickGenerate(t, e)}
                                    data-testid={`template-generate-${t.id}`}
                                    title="One-click generate: creates a draft policy with org name, linked controls, and approver role auto-filled"
                                  >
                                    <Zap className="w-3 h-3 mr-1" />
                                    Generate
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </>
                    );
                  })()}
                </TabsContent>
                
                <TabsContent value="content">
                  <DocumentEditor
                    initialContent={formData.content || '<p>Start writing your policy...</p>'}
                    onContentChange={(content) => setFormData({ ...formData, content })}
                  />
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search policies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterFramework} onValueChange={setFilterFramework}>
          <SelectTrigger className="w-48">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by framework" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Frameworks</SelectItem>
            <SelectItem value="SOC 2">SOC 2</SelectItem>
            <SelectItem value="ISO 27001">ISO 27001</SelectItem>
            <SelectItem value="GDPR">GDPR</SelectItem>
            <SelectItem value="CCPA">CCPA</SelectItem>
            <SelectItem value="PCI DSS">PCI DSS</SelectItem>
            <SelectItem value="USPCF">USPCF</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{policies.length}</p>
                <p className="text-xs text-muted-foreground">Total Policies</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{policies.filter(p => p.status === 'approved').length}</p>
                <p className="text-xs text-muted-foreground">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{policies.filter(p => p.status === 'pending').length}</p>
                <p className="text-xs text-muted-foreground">Pending Review</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <AlertCircle className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{policies.filter(p => p.status === 'draft').length}</p>
                <p className="text-xs text-muted-foreground">Drafts</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Policies Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      ) : filteredPolicies.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {searchTerm || filterFramework !== 'all' 
                ? 'No policies match your search criteria' 
                : 'No policies found. Create your first policy.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPolicies.map((policy) => (
            <Card key={policy.id} className="hover:shadow-md transition-shadow" data-testid={`policy-card-${policy.id}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg font-semibold mb-2 flex items-center gap-2">
                      {getStatusIcon(policy.status)}
                      {policy.title}
                    </CardTitle>
                    <div className="flex gap-2">
                      <Badge variant="outline">{policy.framework}</Badge>
                      <Badge className={getStatusColor(policy.status)}>{policy.status}</Badge>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{policy.description}</p>
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {policy.owner}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1">
                      <History className="w-3 h-3" />
                      v{policy.version || 1}
                    </span>
                    <span>{new Date(policy.updated_at || policy.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                {(policy.approver_role || policy.approver_user_id) && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-4" data-testid={`policy-approver-${policy.id}`}>
                    <CheckCircle className="w-3 h-3 text-emerald-500" />
                    <span>Approver:</span>
                    {policy.approver_role ? (
                      <Badge variant="outline" className="h-5 text-[10px] capitalize">
                        {String(policy.approver_role).replace(/_/g, ' ')}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="h-5 text-[10px]">
                        {(tenantUsers.find(u => u.user_id === policy.approver_user_id)?.full_name) ||
                          (tenantUsers.find(u => u.user_id === policy.approver_user_id)?.email) ||
                          'Assigned user'}
                      </Badge>
                    )}
                  </div>
                )}
                <div className="flex gap-1 flex-wrap">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => handleView(policy)} data-testid={`view-policy-${policy.id}`}>
                    <Eye className="w-3 h-3 mr-1" /> View
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => handleEdit(policy)} data-testid={`edit-policy-${policy.id}`}>
                    <Edit className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleExportPdf(policy)} data-testid={`export-policy-${policy.id}`}>
                    <Download className="w-3 h-3" />
                  </Button>
                  {policy.status === 'draft' && (
                    <Button size="sm" variant="outline" className="text-blue-600 border-blue-200 hover:bg-blue-50" onClick={() => handleSubmitForReview(policy)} data-testid={`submit-policy-${policy.id}`}>
                      <Send className="w-3 h-3" />
                    </Button>
                  )}
                  {(policy.status === 'pending') && (
                    <Button size="sm" variant="outline" className="text-green-600 border-green-200 hover:bg-green-50" onClick={() => handleApprovePolicy(policy.id, 'approved')} data-testid={`approve-policy-${policy.id}`}>
                      <CheckCircle className="w-3 h-3" />
                    </Button>
                  )}
                  {(policy.status === 'pending') && (
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleApprovePolicy(policy.id, 'rejected')} data-testid={`reject-policy-${policy.id}`}>
                      <XCircle className="w-3 h-3" />
                    </Button>
                  )}
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(policy.id)} data-testid={`delete-policy-${policy.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </div>
  );
};

export default PoliciesPage;

function PolicyEditorView({ policy, onBack, onSave, onContentChange, onExport, onSubmitReview, onApprove, getStatusColor }) {
  const [versions, setVersions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);

  const loadVersions = async () => {
    try {
      setLoadingVersions(true);
      const res = await policiesAPI.getVersions(policy.id);
      setVersions(res.data?.versions || []);
    } catch { toast.error('Failed to load versions'); }
    finally { setLoadingVersions(false); }
  };

  const handleRestore = async (versionId) => {
    if (!window.confirm('Restore this version? Current content will be saved as a version.')) return;
    try {
      await policiesAPI.restoreVersion(policy.id, versionId);
      toast.success('Version restored');
      onBack();
    } catch { toast.error('Restore failed'); }
  };

  useEffect(() => { if (showHistory) loadVersions(); }, [showHistory]);

  return (
    <div className="p-6 md:p-8" data-testid="policy-editor-view">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Button variant="ghost" onClick={onBack} className="mb-2" data-testid="back-to-policies">
            ← Back to Policies
          </Button>
          <h1 className="text-2xl font-bold">{policy.title}</h1>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline">{policy.framework}</Badge>
            <Badge className={getStatusColor(policy.status)}>{policy.status}</Badge>
            <Badge variant="secondary" className="text-xs">v{policy.version || 1}</Badge>
            {policy.owner && <span className="text-xs text-muted-foreground">Owner: {policy.owner}</span>}
            {policy.approved_at && <span className="text-xs text-emerald-600">Approved {new Date(policy.approved_at).toLocaleDateString()} by {policy.approved_by}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowHistory(!showHistory)} data-testid="toggle-history-btn">
            <History className="w-4 h-4 mr-1" /> {showHistory ? 'Hide' : 'History'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => onExport(policy)} data-testid="export-pdf-editor-btn">
            <Download className="w-4 h-4 mr-1" /> Export PDF
          </Button>
          {policy.status === 'draft' && (
            <Button variant="outline" size="sm" className="text-blue-600" onClick={() => onSubmitReview(policy)} data-testid="submit-review-editor-btn">
              <Send className="w-4 h-4 mr-1" /> Submit for Review
            </Button>
          )}
          {policy.status === 'approved' && (
            <Button
              variant="outline"
              size="sm"
              className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
              onClick={async () => {
                if (!window.confirm('Publish this policy and notify assigned users?')) return;
                try {
                  const res = await api.post(`/grc/policies/${policy.id}/publish`, {
                    require_acknowledgement: !!policy.require_acknowledgement,
                    assignment_scope: policy.assignment_scope || 'all',
                    assigned_user_ids: policy.assigned_user_ids || [],
                  });
                  toast.success(res.data.message || 'Policy published');
                  onBack();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed to publish');
                }
              }}
              data-testid="publish-editor-btn"
            >
              <Zap className="w-4 h-4 mr-1" /> Publish
            </Button>
          )}
          {policy.status === 'pending' && (
            <>
              <Button variant="outline" size="sm" className="text-green-600" onClick={() => onApprove(policy.id, 'approved')} data-testid="approve-editor-btn">
                <CheckCircle className="w-4 h-4 mr-1" /> Approve
              </Button>
              <Button variant="outline" size="sm" className="text-red-600" onClick={() => onApprove(policy.id, 'rejected')} data-testid="reject-editor-btn">
                <XCircle className="w-4 h-4 mr-1" /> Reject
              </Button>
            </>
          )}
          <Button onClick={() => onSave(policy.content)} data-testid="save-policy-btn">
            <Save className="w-4 h-4 mr-2" /> Save
          </Button>
        </div>
      </div>

      <div className="flex gap-4">
        <div className={showHistory ? 'flex-1' : 'w-full'}>
          <DocumentEditor
            initialContent={policy.content || '<p>Start writing your policy document...</p>'}
            onSave={onSave}
            onContentChange={onContentChange}
          />
        </div>

        {showHistory && (
          <div className="w-80 shrink-0 border rounded-lg p-3 max-h-[calc(100vh-200px)] overflow-y-auto" data-testid="version-history-panel">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <History className="w-4 h-4" /> Version History
            </h3>
            {loadingVersions ? (
              <div className="text-center py-4 text-xs opacity-50">Loading...</div>
            ) : versions.length === 0 ? (
              <p className="text-xs opacity-50">No version history yet. Save content changes to create versions.</p>
            ) : (
              <div className="space-y-2">
                {versions.map((v) => (
                  <div key={v.id} className={`rounded border p-2 text-xs ${v.is_current ? 'border-primary bg-primary/5' : 'hover:bg-muted/50 cursor-pointer'}`}
                    onClick={() => !v.is_current && setSelectedVersion(selectedVersion?.id === v.id ? null : v)}
                    data-testid={`version-${v.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">v{v.version} {v.is_current && <Badge variant="outline" className="text-[10px] ml-1">Current</Badge>}</span>
                      <span className="opacity-40">{v.changed_at ? new Date(v.changed_at).toLocaleString() : ''}</span>
                    </div>
                    <p className="opacity-50 mt-0.5">{v.changed_by}</p>
                    {v.change_summary && <p className="opacity-40 mt-0.5">{v.change_summary}</p>}
                    {selectedVersion?.id === v.id && !v.is_current && (
                      <div className="mt-2 flex gap-1">
                        <Button size="sm" variant="outline" className="text-xs h-6" onClick={(e) => { e.stopPropagation(); handleRestore(v.id); }} data-testid={`restore-version-${v.id}`}>
                          <RotateCcw className="w-3 h-3 mr-1" /> Restore
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
